import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Globe, ArrowLeft, Cookie } from 'lucide-react';
import { Button, LanguageModal } from '@/components/ui';
import { SignInButton, UserButton, useAuth } from '@clerk/clerk-react';
import React from 'react';
import { PageSeo } from '@/components/seo/PageSeo';

function renderInlineBold(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  let remaining = text;
  let key = 0;
  while (remaining.length > 0) {
    const start = remaining.indexOf('**');
    if (start === -1) {
      parts.push(remaining);
      break;
    }
    const end = remaining.indexOf('**', start + 2);
    if (end === -1) {
      parts.push(remaining);
      break;
    }
    if (start > 0) parts.push(remaining.slice(0, start));
    parts.push(<strong key={key++}>{remaining.slice(start + 2, end)}</strong>);
    remaining = remaining.slice(end + 2);
  }
  return parts;
}

function renderTable(rows: string[]): React.ReactNode {
  const parseRow = (row: string) =>
    row.split('|').filter(Boolean).map((cell) => cell.trim());
  const header = parseRow(rows[0]);
  const dataRows = rows.slice(2).map(parseRow); // skip separator row
  return (
    <div className="overflow-x-auto my-3">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr>
            {header.map((cell, i) => (
              <th key={i} className="text-left px-3 py-2 border-b-2 border-[hsl(var(--border))] font-semibold text-[hsl(var(--foreground))]">{cell}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {dataRows.map((row, i) => (
            <tr key={i} className="border-b border-[hsl(var(--border))]">
              {row.map((cell, k) => (
                <td key={k} className="px-3 py-2 text-[hsl(var(--muted-foreground))] font-mono text-xs">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const content = {
  hr: {
    title: 'Politika kolačića',
    lastUpdated: 'Zadnje ažurirano: 13. svibnja 2026.',
    intro: 'Ova politika objašnjava kako BookiApp koristi kolačiće i slične tehnologije.',
    sections: [
      {
        title: '1. Što su kolačići?',
        content: `Kolačići su male tekstualne datoteke koje se pohranjuju na vašem uređaju (računalo, tablet ili mobitel) kada posjetite web stranicu.

Kolačići omogućuju web stranici da zapamti vaše postavke i preference, čineći vaše sljedeće posjete učinkovitijima.`,
      },
      {
        title: '2. Vrste kolačića koje koristimo',
        content: `**Nužni kolačići (Essential)**
Ovi kolačići su neophodni za funkcioniranje web stranice i ne mogu se isključiti.

| Kolačić | Svrha | Trajanje |
|---------|-------|----------|
| __clerk_db_jwt | Autentifikacija korisnika | Sesija |
| __client_uat | Status prijave | Sesija |
| supabase-auth-token | Pristup bazi podataka | 1 sat |

**Funkcionalni kolačići**
Omogućuju dodatne funkcionalnosti poput pamćenja jezika i tema.

| Kolačić | Svrha | Trajanje |
|---------|-------|----------|
| i18next | Odabrani jezik | 1 godina |
| theme | Odabrana tema (svijetla/tamna) | 1 godina |

**Analitički kolačići (opcionalno, s vašom privolom)**
Pomažu nam razumjeti kako posjetitelji koriste naše marketing i blog stranice. Učitavaju se **samo nakon vašeg pristanka** putem cookie banera i **samo na javnim stranicama** (početna, blog, /pricing, /features). Ne učitavaju se unutar aplikacije (dashboard) niti u app za goste.

| Kolačić | Svrha | Trajanje |
|---------|-------|----------|
| _ga | Google Analytics - razlikuje korisnike | 2 godine |
| _ga_5KPBD1LWQW | Google Analytics - stanje sesije | 2 godine |
| ga_consent | Vaš izbor u baneru kolačića | Trajno (localStorage) |

IP adrese se anonimiziraju prije slanja Googleu. Ne dijelimo podatke za oglašavanje.`,
      },
      {
        title: '3. Kolačići trećih strana',
        content: `Koristimo usluge trećih strana koje mogu postavljati vlastite kolačiće:

**Clerk (autentifikacija)**
- Kolačići za sigurnu prijavu i sesiju
- Više informacija: https://clerk.com/privacy

**Stripe (plaćanja)**
- Kolačići za sigurnu obradu plaćanja
- Postavljaju se samo na stranicama plaćanja
- Više informacija: https://stripe.com/privacy

**Google Analytics (statistika posjeta)**
- Samo na javnim marketing i blog stranicama
- Učitava se samo nakon vašeg pristanka
- Anonimizirane IP adrese
- Više informacija: https://policies.google.com/privacy

**Sentry (praćenje grešaka)**
- Bilježi tehničke greške u aplikaciji radi popravka
- Legitimni interes (GDPR čl. 6(1)(f)) - bez praćenja u marketing svrhe
- Više informacija: https://sentry.io/privacy/`,
      },
      {
        title: '4. Upravljanje kolačićima',
        content: `**Putem preglednika:**
Većina preglednika vam omogućuje kontrolu kolačića kroz postavke:

- **Chrome**: Postavke → Privatnost i sigurnost → Kolačići
- **Firefox**: Opcije → Privatnost i sigurnost → Kolačići
- **Safari**: Postavke → Privatnost → Kolačići
- **Edge**: Postavke → Privatnost → Kolačići

**Napomena:**
Ako onemogućite nužne kolačiće, neke funkcionalnosti stranice možda neće raditi ispravno.`,
      },
      {
        title: '5. Local Storage i Session Storage',
        content: `Osim kolačića, koristimo i druge tehnologije pohrane u pregledniku:

**Local Storage:**
- Pohrana korisničkih preferenci
- Offline cache za app za goste

**Session Storage:**
- Privremeni podaci tijekom sesije
- Briše se zatvaranjem kartice`,
      },
      {
        title: '6. Promjene politike',
        content: `Možemo ažurirati ovu politiku kolačića. Promjene će biti objavljene na ovoj stranici s novim datumom ažuriranja.`,
      },
      {
        title: '7. Kontakt',
        content: `Za pitanja o kolačićima:

Email: admin@bookiapp.com`,
      },
    ],
  },
  en: {
    title: 'Cookie Policy',
    lastUpdated: 'Last updated: May 13, 2026',
    intro: 'This policy explains how BookiApp uses cookies and similar technologies.',
    sections: [
      {
        title: '1. What are Cookies?',
        content: `Cookies are small text files that are stored on your device (computer, tablet or phone) when you visit a website.

Cookies allow the website to remember your settings and preferences, making your subsequent visits more efficient.`,
      },
      {
        title: '2. Types of Cookies We Use',
        content: `**Essential Cookies**
These cookies are necessary for the website to function and cannot be disabled.

| Cookie | Purpose | Duration |
|--------|---------|----------|
| __clerk_db_jwt | User authentication | Session |
| __client_uat | Login status | Session |
| supabase-auth-token | Database access | 1 hour |

**Functional Cookies**
Enable additional functionality such as remembering language and theme preferences.

| Cookie | Purpose | Duration |
|--------|---------|----------|
| i18next | Selected language | 1 year |
| theme | Selected theme (light/dark) | 1 year |

**Analytics Cookies (optional, with your consent)**
Help us understand how visitors use our marketing and blog pages. These cookies load **only after your consent** via the cookie banner and **only on public pages** (homepage, blog, /pricing, /features). They are NOT loaded inside the app (dashboard) or in the guest portal.

| Cookie | Purpose | Duration |
|--------|---------|----------|
| _ga | Google Analytics - distinguishes users | 2 years |
| _ga_5KPBD1LWQW | Google Analytics - session state | 2 years |
| ga_consent | Your choice in the cookie banner | Persistent (localStorage) |

IP addresses are anonymized before being sent to Google. We do not share data for advertising purposes.`,
      },
      {
        title: '3. Third-Party Cookies',
        content: `We use third-party services that may set their own cookies:

**Clerk (authentication)**
- Cookies for secure login and session
- More info: https://clerk.com/privacy

**Stripe (payments)**
- Cookies for secure payment processing
- Only set on payment pages
- More info: https://stripe.com/privacy

**Google Analytics (visit statistics)**
- Only on public marketing and blog pages
- Loaded only after your consent
- IP addresses are anonymized
- More info: https://policies.google.com/privacy

**Sentry (error tracking)**
- Logs technical errors in the application for debugging
- Legitimate interest (GDPR Art. 6(1)(f)) - no marketing tracking
- More info: https://sentry.io/privacy/`,
      },
      {
        title: '4. Managing Cookies',
        content: `**Through your browser:**
Most browsers allow you to control cookies through settings:

- **Chrome**: Settings → Privacy and security → Cookies
- **Firefox**: Options → Privacy & Security → Cookies
- **Safari**: Preferences → Privacy → Cookies
- **Edge**: Settings → Privacy → Cookies

**Note:**
If you disable essential cookies, some website functionality may not work properly.`,
      },
      {
        title: '5. Local Storage and Session Storage',
        content: `In addition to cookies, we use other browser storage technologies:

**Local Storage:**
- Storing user preferences
- Offline cache for Guest App

**Session Storage:**
- Temporary data during session
- Cleared when tab is closed`,
      },
      {
        title: '6. Policy Changes',
        content: `We may update this cookie policy. Changes will be posted on this page with a new update date.`,
      },
      {
        title: '7. Contact',
        content: `For questions about cookies:

Email: admin@bookiapp.com`,
      },
    ],
  },
};

export function CookiesPage() {
  const { i18n } = useTranslation();
  const lang = (i18n.language === 'hr' ? 'hr' : 'en') as 'hr' | 'en';
  const t = content[lang];

  const [langOpen, setLangOpen] = useState(false);
  const { isSignedIn } = useAuth();

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <PageSeo path="/cookies" title={`${t.title} | BookiApp`} description={t.intro} />
      {/* Header */}
      <header className="border-b">
        <div className="container mx-auto flex items-center justify-between p-4">
          <Link to="/" className="flex items-center gap-2">
            <img src="/favicon-original.png" alt="BookiApp" className="h-8 w-auto" />
          </Link>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setLangOpen(true)} aria-haspopup="dialog" aria-expanded={langOpen}>
              <Globe className="h-4 w-4 mr-2" />
              {i18n.language.toUpperCase()}
            </Button>
            {isSignedIn ? (
              <>
                <Link to="/dashboard">
                  <Button size="sm" className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white border-0">
                    Dashboard
                  </Button>
                </Link>
                <UserButton
                  afterSignOutUrl="/"
                  appearance={{ elements: { avatarBox: 'h-9 w-9 rounded-xl ring-2 ring-transparent hover:ring-cyan-200 transition-all duration-200' } }}
                />
              </>
            ) : (
              <SignInButton mode="modal" forceRedirectUrl="/dashboard" signUpForceRedirectUrl="/dashboard">
                <Button variant="ghost" size="sm" className="hover:bg-cyan-500/10">
                  {lang === 'hr' ? 'Prijava' : 'Sign In'}
                </Button>
              </SignInButton>
            )}
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 py-12 max-w-3xl">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] mb-8"
        >
          <ArrowLeft className="h-4 w-4" />
          {lang === 'hr' ? 'Natrag' : 'Back'}
        </Link>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500/10 to-blue-500/10 flex items-center justify-center">
            <Cookie className="h-6 w-6 text-cyan-500" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">{t.title}</h1>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">{t.lastUpdated}</p>
          </div>
        </div>

        <p className="text-[hsl(var(--muted-foreground))] mb-8">{t.intro}</p>

        <div className="space-y-8">
          {t.sections.map((section, i) => (
            <section key={i}>
              <h2 className="text-xl font-semibold mb-3">{section.title}</h2>
              <div className="prose prose-sm dark:prose-invert max-w-none">
                {(() => {
                  const lines = section.content.split('\n');
                  const elements: React.ReactNode[] = [];
                  let idx = 0;
                  while (idx < lines.length) {
                    const line = lines[idx];
                    if (line.startsWith('|')) {
                      // Collect all consecutive table rows
                      const tableRows: string[] = [];
                      while (idx < lines.length && lines[idx].startsWith('|')) {
                        tableRows.push(lines[idx]);
                        idx++;
                      }
                      elements.push(<React.Fragment key={idx}>{renderTable(tableRows)}</React.Fragment>);
                      continue;
                    }
                    if (!line.trim()) { idx++; continue; }
                    if (line.startsWith('- ')) {
                      elements.push(<p key={idx} className="ml-4 text-[hsl(var(--muted-foreground))]">- {renderInlineBold(line.slice(2))}</p>);
                    } else if (line.includes('**')) {
                      elements.push(<p key={idx} className="text-[hsl(var(--muted-foreground))] mt-2">{renderInlineBold(line)}</p>);
                    } else {
                      elements.push(<p key={idx} className="text-[hsl(var(--muted-foreground))]">{line}</p>);
                    }
                    idx++;
                  }
                  return elements;
                })()}
              </div>
            </section>
          ))}
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t py-8 mt-12">
        <div className="container mx-auto px-4 text-center text-sm text-[hsl(var(--muted-foreground))]">
          <p>&copy; 2026 BookiApp. {lang === 'hr' ? 'Sva prava pridržana.' : 'All rights reserved.'}</p>
        </div>
      </footer>

      <LanguageModal open={langOpen} onClose={() => setLangOpen(false)} scope="marketing" />
    </div>
  );
}

export default CookiesPage;
